import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Search, User, Users, TrendingUp, 
  Calendar, Clock, Eye, Trash2, AlertTriangle, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { userService } from '../services/userService';

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  
  // Summary statistics
  const [visitorStats, setVisitorStats] = useState({ total: 0, period: 0 });
  const [signupStats, setSignupStats] = useState({ total: 0, period: 0 });

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  
  // Sorting state for Last Visited column
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first

  // Helper function to sort users by last_visited
  const sortUsersByLastVisited = (usersList, order) => {
    return [...usersList].sort((a, b) => {
      const dateA = a.last_visited ? new Date(a.last_visited) : new Date(0);
      const dateB = b.last_visited ? new Date(b.last_visited) : new Date(0);
      
      if (order === 'desc') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [timeFilter, users]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = members.filter(user =>
        user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
      // Apply sorting
      const sorted = sortUsersByLastVisited(filtered, sortOrder);
      setFilteredMembers(sorted);
    } else {
      // Apply sorting to all members
      const sorted = sortUsersByLastVisited(members, sortOrder);
      setFilteredMembers(sorted);
    }
  }, [searchTerm, members, sortOrder]);

  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await userService.getAllUsers();
      console.log('Fetched users:', data);
      
      setUsers(data);
      
      // Filter only members (non-admin users) - exclude all admin users
      const memberList = data.filter(u => 
        !u.is_admin && 
        u.role !== 'admin' && 
        u.role !== 'superadmin' &&
        (u.role === 'member' || u.role === 'user' || !u.role)
      );
      
      setMembers(memberList);
      setFilteredMembers(memberList);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      // Fetch visitor analytics
      const visitors = await userService.getVisitorAnalytics(timeFilter);
      setVisitorStats(visitors);

      // Fetch signup analytics
      const signups = await userService.getSignupAnalytics(timeFilter);
      setSignupStats(signups);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (user) => {
    if (user.profile_completed) {
      return <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Active</span>;
    }
    return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">Incomplete</span>;
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
    setShowFinalConfirm(false);
    setAdminPassword('');
    setDeleteError('');
  };

  const handlePasswordSubmit = () => {
    // Validate password
    if (!adminPassword || adminPassword.trim().length === 0) {
      setDeleteError('Please enter your admin password');
      return;
    }
    // Show final confirmation
    setDeleteError('');
    setShowFinalConfirm(true);
  };

  const handleFinalDelete = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    setDeleteError('');
    
    try {
      console.log('🗑️ Attempting to delete user:', userToDelete.id);
      const result = await userService.deleteUser(userToDelete.id, adminPassword);
      
      // Success - close dialog and show success message
      setShowDeleteDialog(false);
      toast.success(`User ${userToDelete.first_name} ${userToDelete.last_name} removed successfully!`, {
        duration: 3000,
        position: 'top-center',
      });
      
      // Refresh the user list
      await fetchAllData();
      
      // If there's a backend limitation note, show additional info
      if (result.note) {
        console.warn('Backend limitation:', result.note);
        setTimeout(() => {
          toast('Note: Backend deletion endpoint not available', {
            duration: 2000,
            position: 'top-center',
            icon: '⚠️',
          });
        }, 1500);
      }
      
    } catch (err) {
      console.error('❌ Error deleting user:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      // Show error message
      let errorMsg = 'Failed to delete user. Please try again.';
      
      if (err.response?.status === 405) {
        errorMsg = 'User deletion not supported by backend. Contact system administrator.';
      } else if (err.response?.status === 401) {
        errorMsg = 'Incorrect admin password. Please try again.';
      } else if (err.response?.status === 404) {
        errorMsg = 'User not found or already deleted.';
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setDeleteError(errorMsg);
      toast.error(errorMsg, {
        duration: 4000,
        position: 'top-center',
      });
      
      // Reset to password entry on auth errors
      if (err.response?.status === 401 || errorMsg.toLowerCase().includes('password')) {
        setShowFinalConfirm(false);
        setAdminPassword('');
      }
    } finally {
      setDeleting(false);
    }
  };

  const resetDeleteFlow = () => {
    setShowDeleteDialog(false);
    setShowFinalConfirm(false);
    setAdminPassword('');
    setDeleteError('');
    setDeleting(false);
    setUserToDelete(null);
  };

  const getFilterLabel = () => {
    switch(timeFilter) {
      case 'today': return 'Today';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      default: return 'All Time';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-start justify-between gap-3 sm:gap-4">
              <div className="flex items-start gap-3 sm:gap-4 flex-1">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 shrink-0"
                  size="sm"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden sm:inline">Back</span>
                </Button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">User Management</h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Manage website visitors, member accounts and signups
                  </p>
                </div>
              </div>
              
              {/* Today's Date - Extreme Right */}
              <div className="flex items-center text-blue-600 font-semibold text-sm whitespace-nowrap">
                <Calendar size={16} className="mr-2" />
                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            </div>

            {/* Time Filter Buttons - Responsive */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAllData}
                className="flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant={timeFilter === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeFilter('today')}
                className="text-xs sm:text-sm"
              >
                Today
              </Button>
              <Button
                variant={timeFilter === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeFilter('month')}
                className="text-xs sm:text-sm"
              >
                This Month
              </Button>
              <Button
                variant={timeFilter === 'year' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeFilter('year')}
                className="text-xs sm:text-sm"
              >
                This Year
              </Button>
              <Button
                variant={timeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeFilter('all')}
                className="text-xs sm:text-sm"
              >
                All Time
              </Button>
              
              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  console.log('🔄 Manual refresh triggered');
                  fetchAllData();
                }}
                className="text-xs sm:text-sm ml-4"
                disabled={loading}
              >
                🔄 {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Summary Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* New Website Visitors */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">New Website Visitors</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {timeFilter === 'all' ? visitorStats.total : visitorStats.period}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{getFilterLabel()}</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            {/* Account Created / Signed Up */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Accounts Created</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {timeFilter === 'all' ? signupStats.total : signupStats.period}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getFilterLabel()} · Out of {timeFilter === 'all' ? visitorStats.total : visitorStats.period} visitors
                    </p>
                  </div>
                  <Users className="w-12 h-12 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        ) : (
          <>
            {/* Search Bar for User List */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search users by name, email, or phone number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* User List Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  User List ({filteredMembers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredMembers.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 text-sm">
                    {searchTerm ? 'No users found matching your search.' : 'No users yet.'}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Name
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Email
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Phone
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                              }}
                              className="flex items-center gap-1 hover:text-gray-700 transition-colors cursor-pointer"
                            >
                              Last Visited
                              {sortOrder === 'desc' ? (
                                <ArrowDown size={14} className="text-blue-600" />
                              ) : (
                                <ArrowUp size={14} className="text-blue-600" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Status
                          </th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredMembers.map((user) => (
                          <tr
                            key={user.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleUserClick(user.id)}
                          >
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 bg-gray-200 rounded-full flex items-center justify-center">
                                  <User className="h-4 w-4 sm:h-6 sm:w-6 text-gray-600" />
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                    {user.first_name} {user.last_name || user.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                              {user.email}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                              {user.phone || 'N/A'}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                              {user.last_visited ? formatDate(user.last_visited) : formatDate(user.created_at)}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              {getStatusBadge(user)}
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUserClick(user.id)}
                                  }
                                  className="flex items-center gap-1 sm:gap-2 text-xs"
                                >
                                  <Eye size={12} className="sm:w-3.5 sm:h-3.5" />
                                  <span className="hidden sm:inline">View</span>
                                  <span className="sm:hidden">View</span>
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(user);
                                  }}
                                  className="flex items-center gap-1 sm:gap-2 text-xs"
                                >
                                  <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
                                  <span className="hidden sm:inline">Delete</span>
                                  <span className="sm:hidden">Del</span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Toast Container */}
      <Toaster />

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => !deleting && setShowDeleteDialog(open)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {!showFinalConfirm ? 'Delete User - Enter Password' : 'Delete User - Final Confirmation'}
            </DialogTitle>
            <DialogDescription>
              {!showFinalConfirm ? (
                <>
                  You are about to delete <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>.
                  Please enter your admin password to continue.
                </>
              ) : (
                <>
                  Are you sure you want to permanently delete this user? This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Password Entry */}
            {!showFinalConfirm && (
              <div>
                <Label htmlFor="admin-password" className="text-sm font-medium">
                  Admin Password
                </Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => {
                    setAdminPassword(e.target.value);
                    setDeleteError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && adminPassword) {
                      handlePasswordSubmit();
                    }
                  }}
                  placeholder="Enter your admin password"
                  className="mt-2"
                  disabled={deleting}
                  autoFocus
                />
              </div>
            )}

            {/* Final Confirmation */}
            {showFinalConfirm && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 font-medium">
                  ⚠️ This will permanently remove <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong> from the system.
                </p>
                <p className="text-sm text-red-600 mt-2">
                  Type "DELETE" to confirm:
                </p>
              </div>
            )}

            {/* Error Message */}
            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600 font-medium">{deleteError}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={resetDeleteFlow}
              disabled={deleting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            
            {!showFinalConfirm ? (
              <Button 
                variant="destructive" 
                onClick={handlePasswordSubmit}
                disabled={!adminPassword || deleting}
                className="w-full sm:w-auto"
              >
                Continue
              </Button>
            ) : (
              <Button 
                variant="destructive" 
                onClick={handleFinalDelete}
                disabled={deleting}
                className="w-full sm:w-auto"
              >
                {deleting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete User Permanently'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
